import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { getToken, getUserId } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";

const MONTHS = [
  { label: "มิถุนายน",  num: 6  },
  { label: "กรกฎาคม",   num: 7  },
  { label: "พฤศจิกายน", num: 11 },
  { label: "ธันวาคม",   num: 12 },
];

const calcBMI = (height, weight) => {
  const h = parseFloat(height);
  const w = parseFloat(weight);
  if (!h || !w || h <= 0) return null;
  return w / ((h / 100) ** 2);
};

const bmiLabel = (bmi) => {
  if (bmi === null) return "-";
  const label =
    bmi < 18.5 ? "น้ำหนักน้อย" :
    bmi < 23   ? "ปกติ" :
    bmi < 27.5 ? "น้ำหนักเกิน" : "อ้วน";
  return `${bmi.toFixed(1)} (${label})`;
};

const bmiColor = (bmi) => {
  if (bmi === null) return "inherit";
  if (bmi < 18.5 || bmi >= 27.5) return "#dc3545";
  if (bmi >= 23) return "#e67e22";
  return "#198754";
};

export default function StudentBodyMass() {
  const { setLoading } = useLoading();
  const advisorId = getUserId();

  const [yearList, setYearList]   = useState([]);
  const [yearId, setYearId]       = useState("");
  const [month, setMonth]         = useState(6);
  const [students, setStudents]   = useState([]);
  const [data, setData]           = useState({});
  const [loaded, setLoaded]       = useState(false);
  const [dirty, setDirty]         = useState(false);
  const [saving, setSaving]       = useState(false);

  // Load year list on mount
  useEffect(() => {
    axiosInstance
      .get(`${process.env.REACT_APP_API}/getStudyYear`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((res) => {
        setYearList(res.data || []);
        if (res.data?.length) {
          const saved = localStorage.getItem("selectedYearId");
          setYearId(saved && res.data.find(y => String(y.year_id) === saved)
            ? saved
            : String(res.data[0].year_id));
        }
      })
      .catch(console.error);
  }, []);

  const handleLoad = () => {
    if (!yearId) return;
    setLoading(true);
    setLoaded(false);
    axiosInstance
      .get(
        `${process.env.REACT_APP_API}/getBodyMassByClass/${advisorId}/${yearId}/${month}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      )
      .then((res) => {
        setStudents(res.data);
        const init = {};
        res.data.forEach((s) => {
          init[s.st_id] = {
            height: s.stbm_height ?? "",
            weight: s.stbm_weight ?? "",
          };
        });
        setData(init);
        setLoaded(true);
        setDirty(false);
      })
      .catch((err) => {
        console.error(err);
        Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: "ไม่สามารถโหลดข้อมูลได้" });
      })
      .finally(() => setLoading(false));
  };

  const handleChange = (stId, field, val) => {
    setData((prev) => ({
      ...prev,
      [stId]: { ...prev[stId], [field]: val },
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const records = students.map((s) => ({
      studentId: s.st_id,
      height: data[s.st_id]?.height !== "" ? Number(data[s.st_id]?.height) : null,
      weight: data[s.st_id]?.weight !== "" ? Number(data[s.st_id]?.weight) : null,
    }));
    try {
      await axiosInstance.post(
        `${process.env.REACT_APP_API}/saveBodyMassBatch`,
        { yearId: Number(yearId), month: Number(month), records },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      Swal.fire({ icon: "success", title: "บันทึกสำเร็จ", timer: 1500, showConfirmButton: false });
      setDirty(false);
    } catch {
      Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: "ไม่สามารถบันทึกข้อมูลได้" });
    } finally {
      setSaving(false);
    }
  };

  const selectedMonthLabel = MONTHS.find((m) => m.num === month)?.label || "";
  const selectedYear = yearList.find((y) => String(y.year_id) === String(yearId));

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">

        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">
                <Link to="#">ข้อมูลประจำชั้น</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                บันทึกส่วนสูงและน้ำหนัก
              </li>
            </ol>
            <h4 className="main-title mb-0">บันทึกส่วนสูงและน้ำหนัก</h4>
          </div>
        </div>

        {/* Filter bar */}
        <Card className="card-one mb-4">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col xs={12} md={4}>
                <Form.Label className="fw-semibold">ปีการศึกษา</Form.Label>
                <Form.Select
                  value={yearId}
                  onChange={(e) => {
                    setYearId(e.target.value);
                    localStorage.setItem("selectedYearId", e.target.value);
                    setLoaded(false);
                  }}
                >
                  {yearList.map((y) => (
                    <option key={y.year_id} value={y.year_id}>
                      {y.year_year}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={4}>
                <Form.Label className="fw-semibold">เดือน</Form.Label>
                <Form.Select
                  value={month}
                  onChange={(e) => { setMonth(Number(e.target.value)); setLoaded(false); }}
                >
                  {MONTHS.map((m) => (
                    <option key={m.num} value={m.num}>
                      {m.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={4}>
                <Button variant="primary" onClick={handleLoad}>
                  <i className="ri-search-line me-1"></i>โหลดข้อมูล
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Student table */}
        {loaded && (
          <Card className="card-one">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <Card.Title as="h6" className="mb-0">
                {`ปีการศึกษา ${selectedYear?.year_year || ""} — เดือน${selectedMonthLabel}`}
                <span className="text-muted fw-normal ms-2 fs-sm">
                  ({students.length} คน)
                </span>
              </Card.Title>
              <Button
                variant="success"
                disabled={!dirty || saving}
                onClick={handleSave}
              >
                <i className="ri-save-line me-1"></i>
                {saving ? "กำลังบันทึก..." : "บันทึกทั้งหมด"}
              </Button>
            </Card.Header>

            <Card.Body className="p-0">
              {students.length === 0 ? (
                <div className="text-center text-muted py-5">ไม่พบนักเรียนในชั้นเรียน</div>
              ) : (
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-secondary">
                    <tr>
                      <th className="text-center" style={{ width: 56 }}>ลำดับ</th>
                      <th style={{ width: 130 }}>รหัสนักเรียน</th>
                      <th>ชื่อ-สกุล</th>
                      <th style={{ width: 160 }} className="text-center">
                        ส่วนสูง&nbsp;<small className="fw-normal">(ซม.)</small>
                      </th>
                      <th style={{ width: 160 }} className="text-center">
                        น้ำหนัก&nbsp;<small className="fw-normal">(กก.)</small>
                      </th>
                      <th style={{ width: 200 }} className="text-center">ดัชนีมวลกาย</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s, idx) => {
                      const h = data[s.st_id]?.height;
                      const w = data[s.st_id]?.weight;
                      const bmi = calcBMI(h, w);
                      return (
                        <tr key={s.st_id}>
                          <td className="text-center text-muted">{idx + 1}</td>
                          <td>{s.st_idnumber}</td>
                          <td>{s.st_name}</td>
                          <td className="text-center" style={{ padding: "4px 8px" }}>
                            <Form.Control
                              type="number"
                              size="sm"
                              className="text-center"
                              value={h ?? ""}
                              onChange={(e) => handleChange(s.st_id, "height", e.target.value)}
                              placeholder="—"
                              min={0}
                              max={250}
                            />
                          </td>
                          <td className="text-center" style={{ padding: "4px 8px" }}>
                            <Form.Control
                              type="number"
                              size="sm"
                              className="text-center"
                              value={w ?? ""}
                              onChange={(e) => handleChange(s.st_id, "weight", e.target.value)}
                              placeholder="—"
                              min={0}
                              max={200}
                            />
                          </td>
                          <td className="text-center fw-semibold" style={{ color: bmiColor(bmi) }}>
                            {bmiLabel(bmi)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>

            {dirty && (
              <Card.Footer className="d-flex justify-content-end bg-transparent">
                <Button
                  variant="success"
                  disabled={saving}
                  onClick={handleSave}
                >
                  <i className="ri-save-line me-1"></i>
                  {saving ? "กำลังบันทึก..." : "บันทึกทั้งหมด"}
                </Button>
              </Card.Footer>
            )}
          </Card>
        )}

      </div>
      <Footer />
    </>
  );
}
